/**
 * O que faz: botão de avisos dos agentes na barra lateral (leads esperando a equipe e sugestões para revisar).
 * Onde usa: Sidebar (rodapé), para todas as páginas autenticadas.
 * Conexões: useAvisosAgentes (RPC contar_avisos_agentes, só contagens), rotas /chat e /ia?aba=agentes&revisar=1 (abre o modal de revisão).
 * Tabelas: indiretamente lead_classification_state e lead_return_suggestions (agregados por clínica).
 * Data: 25/09/2026.
 */
import { Link } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useAvisosAgentes } from '../useAgentes';

interface SinoAvisosAgentesProps {
  clinicaId: string | null;
  modoAdmin: boolean;
  recolhida?: boolean;
}

export function SinoAvisosAgentes({ clinicaId, modoAdmin, recolhida = false }: SinoAvisosAgentesProps) {
  const avisos = useAvisosAgentes(clinicaId, modoAdmin);
  const dados = avisos.data;
  // Sem agentes ligados (ou plano sem agentes) o botão não aparece: nada a avisar.
  if (!dados?.ativo) return null;

  const paraRevisar = dados.retornosParaRevisar + dados.etapasParaRevisar;
  const total = dados.aguardandoEquipeAtrasados + paraRevisar;
  const rotulo = total > 0 ? `Avisos dos agentes: ${total}` : 'Avisos dos agentes: nenhum';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={rotulo}
          className={cn(
            'relative flex min-h-9 w-full items-center gap-2 rounded-xl pl-[14px] text-sm text-muted-foreground transition-colors hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sidebar-ring motion-reduce:transition-none',
            recolhida && 'justify-center pl-0',
          )}
        >
          <Bell aria-hidden size={16} />
          <span className={cn(recolhida && 'sr-only')}>Avisos</span>
          {total > 0 && (
            <span className={cn('ml-auto mr-3 rounded-full bg-primary px-1.5 text-[11px] font-semibold leading-5 text-primary-foreground', recolhida && 'absolute -right-1 -top-1 m-0')}>
              {total > 99 ? '99+' : total}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent side="right" align="end" className="w-72 space-y-3 text-sm">
        <p className="font-medium text-foreground">Avisos dos agentes</p>
        <ul className="space-y-2">
          <li>
            <Link to="/chat" className="block rounded-lg p-2 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <span className="font-medium">{dados.aguardandoEquipeAtrasados}</span> lead(s) esperando resposta há mais de {dados.limiteEsperaMinutos} min
              <span className="block text-xs text-muted-foreground">{dados.aguardandoEquipe} aguardando a equipe no total</span>
            </Link>
          </li>
          <li>
            <Link to="/ia?aba=agentes&revisar=1" className="block rounded-lg p-2 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              <span className="font-medium">{paraRevisar}</span> sugestão(ões) para revisar
              <span className="block text-xs text-muted-foreground">{dados.retornosParaRevisar} retorno(s) e {dados.etapasParaRevisar} mudança(s) de etapa</span>
            </Link>
          </li>
        </ul>
      </PopoverContent>
    </Popover>
  );
}
